import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function camelToTitleCase(s: string): string {
	const result = s.replace(/([A-Z])/g, " $1");
	return (result.charAt(0).toUpperCase() + result.slice(1)).trim();
}

export function titleCase(s: string): string {
	return s
		.split(" ")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");
}

export function snakeCaseToKebabCase(s: string): string {
	return s.replace(/_/g, "-").toLowerCase();
}

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

function isNonAppleMobileDevice(): boolean {
	if (typeof navigator === "undefined") return false;

	const ua = navigator.userAgent ?? navigator.vendor ?? "";
	return /android|iemobile|mobile|blackberry|bb10|mini|windows phone|webos|opera mini/i.test(
		ua,
	);
}

function isIos(): boolean {
	if (typeof navigator === "undefined") return false;

	const ua = navigator.userAgent ?? navigator.vendor ?? "";
	// @ts-ignore
	return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isIPad(): boolean {
	const platform = navigator.platform;
	const isMacLike = platform === "MacIntel"; // iPads report this in desktop mode
	const hasTouch = Boolean(
		navigator.maxTouchPoints && navigator.maxTouchPoints > 1,
	);
	return isMacLike && hasTouch;
}

export function isAppleMobile(): boolean {
	return isIos() || isIPad();
}

export function isMobileDevice(): boolean {
	return isNonAppleMobileDevice() || isAppleMobile();
}

export const canPlayType = (mimeType: string): boolean => {
	const audio = document.createElement("audio");
	const canPlay = audio.canPlayType(mimeType);
	const result = canPlay === "probably";

	return result;
};

export const getBaseName = (slug: string) => {
	const brainWaveSlugs = ["gamma-", "beta-", "alpha-", "theta-", "delta-"];
	// Check if the slug starts with any of the brainwave prefixes
	if (brainWaveSlugs.some((prefix) => slug.startsWith(prefix))) {
		return slug.split("-")[0]; // Return the prefix only for brainwave tracks
	}
	return slug; // Return the full slug for other tracks
};

export type PrettyDateOptions = {
	includeTime?: boolean;
};

export const prettyDate = (
	date: Date | string,
	options: PrettyDateOptions = {
		includeTime: true,
	},
): string => {
	const d = new Date(date);
	const formatOptions: Intl.DateTimeFormatOptions = {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	};
	if (options?.includeTime) {
		formatOptions.hour = "2-digit";
		formatOptions.minute = "2-digit";
		formatOptions.hour12 = true; // Use 12-hour format with AM/PM
	}

	return d.toLocaleDateString("en-US", formatOptions);
};
